/**
 * Customers → budgets → issuances.
 *
 * Functional spec: `qt_poc/customers_qt.py`.
 *
 * Two rules this screen exists to make visible:
 *
 *   1. **A null balance is not zero.** `/budgets/{id}/balance` returns `null`
 *      for `issued`/`balance` when the computation fails, with `error` saying
 *      why. Rendering that as 0 would tell someone their entire budget is
 *      available — the exact defect (CRIT-001) that made the API nullable in
 *      the first place. Nulls render as a warning, never as a number.
 *   2. **Only ISSUED/CORRECTED issuances draw on a budget.** A DRAFT is a plan.
 *      The status column is therefore not decoration; it is the difference
 *      between money committed and money considered.
 */
import { useMemo, useState } from 'react'
import {
  ActionIcon, Alert, Button, Card, Group, NumberInput, Paper, Select,
  SimpleGrid, Stack, Tabs, Text, TextInput, Textarea, Title, Tooltip,
} from '@mantine/core'
import { useLocalStorage } from '@mantine/hooks'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import type { ColumnDef } from '@tanstack/react-table'

import { ApiError, api } from '../api/client'
import type {
  BudgetBalance, Customer, CustomerBudget, CustomerSummary, Issuance,
} from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { Modal } from '../components/AppModal'
import { DataGrid } from '../components/DataGrid'
import { StatusBadge } from '../components/StatusBadge'
import { EM_DASH, fmtDate, fmtMoney, fmtPct } from '../format'
import { FILL_PANEL, FILL_REMAINING } from '../theme'
import { confirmDelete } from '../components/confirm'
import { IssuanceEditor } from './IssuanceEditor'

/**
 * A money cell a viewer cannot see (DP-01): the dash, same as any other
 * missing value, but with a tooltip saying *why* it is missing rather than
 * leaving it looking like the budget has no amount at all.
 */
function HiddenAmount() {
  return (
    <Tooltip label="מוצג למנהלים ולעורכים בלבד">
      <Text span c="dimmed" style={{ cursor: 'help' }}>{EM_DASH}</Text>
    </Tooltip>
  )
}

export function CustomersPage() {
  const qc = useQueryClient()
  const { canEdit, canDelete, isViewer } = useAuth()
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [budget, setBudget] = useState<CustomerBudget | null>(null)
  const [editingCustomer, setEditingCustomer] = useState<Customer | null | undefined>(undefined)
  const [editingBudget, setEditingBudget] = useState<CustomerBudget | null | undefined>(undefined)
  // `undefined` = closed, `null` = new draft, an Issuance = editing that one.
  const [editingIssuance, setEditingIssuance] =
    useState<Issuance | null | undefined>(undefined)
  const [loadingIssuance, setLoadingIssuance] = useState(false)
  // Which detail tab (budgets/issuances) is open, persisted the same way
  // `FilterPanel`'s expand/collapse state is — `useLocalStorage`, not a
  // hand-rolled read/write, so the private-browsing/blocked-storage guard
  // lives in one place for the whole app. Switching customers does NOT
  // reset this — only picking a budget does (below), the same way the old
  // vertical stack always showed budgets-then-issuances in that order.
  const [detailTab, setDetailTab] = useLocalStorage({
    key: 'qtapp-customers-detail-tab',
    defaultValue: 'budgets',
  })

  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.get<Customer[]>('/customers'),
  })

  const budgets = useQuery({
    queryKey: ['budgets', customer?.id],
    queryFn: () => api.get<CustomerBudget[]>(`/customers/${customer!.id}/budgets`),
    enabled: customer?.id != null,
  })

  /**
   * The customer-level rollup, across all their ACTIVE budgets.
   *
   * Worth its own request rather than summing the budget list in the browser:
   * each issuance converts at the rate frozen *on that issuance*, which the
   * budget rows do not carry. A client-side sum would use today's rate and
   * quietly restate history.
   */
  const summary = useQuery({
    queryKey: ['customer-summary', customer?.id],
    queryFn: () => api.get<CustomerSummary>(`/customers/${customer!.id}/summary`),
    enabled: customer?.id != null,
  })

  const balance = useQuery({
    queryKey: ['balance', budget?.id],
    queryFn: () => api.get<BudgetBalance>(`/budgets/${budget!.id}/balance`),
    enabled: budget?.id != null,
  })

  const issuances = useQuery({
    queryKey: ['issuances', budget?.id],
    queryFn: () => api.get<Issuance[]>(`/budgets/${budget!.id}/issuances`),
    enabled: budget?.id != null,
  })

  /**
   * Open one issuance in the editor.
   *
   * The list endpoints (`get_for_budget`) return issuance headers with an
   * **empty `lines`** — only `GET /issuances/{id}` loads them. Handing the row
   * straight to the editor would therefore open every issuance as if it had no
   * lines, and saving it would delete them all (`IssuanceDAO.save` replaces the
   * line set wholesale).
   */
  async function openIssuance(row: Issuance) {
    setLoadingIssuance(true)
    try {
      setEditingIssuance(await api.get<Issuance>(`/issuances/${row.id}`))
    } catch (err) {
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? err.message : 'טעינת הניפוק נכשלה',
      })
    } finally {
      setLoadingIssuance(false)
    }
  }

  // Deletes here are refused server-side while history exists: a customer
  // with posted issuances, a budget with any issuance at all. The refusal
  // names the alternative (archive it, close it), so it is shown verbatim
  // rather than replaced with "delete failed".
  function deleteReporter(done: string) {
    return {
      onSuccess: () => {
        void qc.invalidateQueries()
        notifications.show({ message: done, color: 'green' })
      },
      onError: (err: unknown) =>
        notifications.show({
          color: 'red',
          message: err instanceof ApiError ? err.message : 'המחיקה נכשלה',
        }),
    }
  }

  const removeCustomer = useMutation({
    mutationFn: (id: number) => api.delete<unknown>(`/customers/${id}`),
    ...deleteReporter('הלקוח נמחק'),
  })
  const removeBudget = useMutation({
    mutationFn: (id: number) => api.delete<unknown>(`/budgets/${id}`),
    ...deleteReporter('התקציב נמחק'),
  })

  const removeIssuance = useMutation({
    mutationFn: (id: number) => api.delete<unknown>(`/issuances/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['issuances', budget?.id] })
      void qc.invalidateQueries({ queryKey: ['balance', budget?.id] })
      notifications.show({ message: 'הניפוק נמחק', color: 'green' })
    },
    onError: (err) =>
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? err.message : 'מחיקת הניפוק נכשלה',
      }),
  })

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      api.post<Issuance>(`/issuances/${id}/status`, { status, notes: '' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['issuances', budget?.id] })
      void qc.invalidateQueries({ queryKey: ['balance', budget?.id] })
      notifications.show({ message: 'סטטוס הניפוק עודכן', color: 'green' })
    },
    onError: (err) =>
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? err.message : 'עדכון הסטטוס נכשל',
      }),
  })

  const customerColumns = useMemo<ColumnDef<Customer, any>[]>(
    () => [
      { accessorKey: 'name', header: 'שם' },
      { accessorKey: 'external_id', header: 'מזהה', meta: { code: true, hideBelow: 'lg' }, cell: (c) => c.getValue<string>() || EM_DASH },
      { accessorKey: 'contact_name', header: 'איש קשר', meta: { hideBelow: 'lg' }, cell: (c) => c.getValue<string>() || EM_DASH },
      { accessorKey: 'contact_phone', header: 'טלפון', meta: { code: true, hideBelow: 'md' }, cell: (c) => c.getValue<string>() || EM_DASH },
      { accessorKey: 'status', header: 'סטטוס', cell: (c) => <StatusBadge value={c.getValue<string>()} /> },
      {
        id: 'act',
        header: '',
        meta: { nowrap: true, actions: true },
        cell: (c) => (
          <Group gap={4} wrap="nowrap" onClick={(e) => e.stopPropagation()}>
            {canEdit && (
              <Button
                size="xs"
                variant="subtle"
                onClick={() => setEditingCustomer(c.row.original)}
              >
                ערוך
              </Button>
            )}
            {canDelete && (
              <ActionIcon
                size={30} color="red" variant="subtle" aria-label="מחק לקוח"
                loading={removeCustomer.isPending &&
                         removeCustomer.variables === c.row.original.id}
                onClick={() => {
                  void confirmDelete(`הלקוח "${c.row.original.name}"`).then(
                    (ok) => ok && removeCustomer.mutate(c.row.original.id!))
                }}
              >
                ✕
              </ActionIcon>
            )}
          </Group>
        ),
      },
    ],
    [canEdit, canDelete, removeCustomer],
  )

  const budgetColumns = useMemo<ColumnDef<CustomerBudget, any>[]>(
    () => [
      { accessorKey: 'name', header: 'שם תקציב' },
      { accessorKey: 'currency', header: 'מטבע', meta: { hideBelow: 'lg' } },
      {
        accessorKey: 'original_amount',
        header: 'תקציב מקורי',
        meta: { numeric: true },
        // Null for a viewer (DP-01) — `fmtMoney` already renders that as the
        // dash; swap in the tooltipped one so it says why.
        cell: (c) => {
          const value = c.getValue<number | null>()
          return value == null && isViewer
            ? <HiddenAmount />
            : fmtMoney(value, c.row.original.currency)
        },
      },
      { accessorKey: 'engineering_tariff_pct', header: 'תעריף %', meta: { numeric: true, hideBelow: 'md' }, cell: (c) => fmtPct(c.getValue<number>() * 100) },
      { accessorKey: 'emf_number', header: 'EMF', meta: { code: true, hideBelow: 'lg' }, cell: (c) => c.getValue<string>() || EM_DASH },
      {
        accessorKey: 'status',
        header: 'סטטוס',
        // `sm`, not `md` like the others on this grid: this table sits
        // inside its own padded Paper (nested under a selected customer), so
        // its box is ~24px narrower than a bare grid's at the same viewport
        // — just enough that [name, amount, status, act] still minutely
        // overlapped the sticky actions column on a 390px phone even after
        // `STICKY_NO_STRETCH` (`DataGrid.tsx`) stopped the two pinned
        // columns themselves from stretching to fill it. Status is the one
        // column of the four that is not money, an identifier or the row's
        // actions, so it is the one that gives way at the narrowest size —
        // recoverable from the "עמודות" chooser like any other hidden one.
        meta: { hideBelow: 'sm' },
        cell: (c) => <StatusBadge value={c.getValue<string>()} />,
      },
      {
        id: 'act',
        header: '',
        meta: { nowrap: true, actions: true },
        cell: (c) => (
          <Group gap={4} wrap="nowrap" onClick={(e) => e.stopPropagation()}>
            {canEdit && (
              <Button
                size="xs"
                variant="subtle"
                onClick={() => setEditingBudget(c.row.original)}
              >
                ערוך
              </Button>
            )}
            {canDelete && (
              <ActionIcon
                size={30} color="red" variant="subtle" aria-label="מחק תקציב"
                loading={removeBudget.isPending &&
                         removeBudget.variables === c.row.original.id}
                onClick={() => {
                  void confirmDelete(`התקציב "${c.row.original.name}"`).then(
                    (ok) => ok && removeBudget.mutate(c.row.original.id!))
                }}
              >
                ✕
              </ActionIcon>
            )}
          </Group>
        ),
      },
    ],
    [canEdit, canDelete, removeBudget, isViewer],
  )

  const issuanceColumns = useMemo<ColumnDef<Issuance, any>[]>(
    () => [
      { accessorKey: 'issuance_number', header: 'מס׳ ניפוק', meta: { code: true } },
      { accessorKey: 'issuance_date', header: 'תאריך', meta: { hideBelow: 'lg' }, cell: (c) => fmtDate(c.getValue<string>()) },
      { accessorKey: 'project_name', header: 'פרויקט', meta: { hideBelow: 'md' }, cell: (c) => c.getValue<string>() || EM_DASH },
      { accessorKey: 'site_name', header: 'אתר', meta: { hideBelow: 'lg' }, cell: (c) => c.getValue<string>() || EM_DASH },
      // `hideBelow: 'sm'` — see `budgetColumns`' own `status` column just
      // above for why this nested grid specifically needs a third column
      // gone at phone width, not just the two every other grid's status
      // column loses.
      { accessorKey: 'status', header: 'סטטוס', meta: { hideBelow: 'sm' }, cell: (c) => <StatusBadge value={c.getValue<string>()} /> },
      {
        accessorKey: 'total_equipment_cost',
        header: 'עלות ציוד',
        meta: { numeric: true, hideBelow: 'md' },
        cell: (c) => fmtMoney(c.getValue<number>(), c.row.original.currency),
      },
      {
        accessorKey: 'total_tariff',
        header: 'תעריף',
        meta: { numeric: true, hideBelow: 'md' },
        cell: (c) => fmtMoney(c.getValue<number>(), c.row.original.currency),
      },
      {
        accessorKey: 'total_value',
        header: 'סה״כ',
        meta: { numeric: true },
        cell: (c) => fmtMoney(c.getValue<number>(), c.row.original.currency),
      },
      {
        id: 'act',
        header: '',
        meta: { nowrap: true, actions: true },
        cell: (c) => {
          const row = c.row.original
          const draft = row.status === 'DRAFT'
          return (
            <Group gap={4} wrap="nowrap" onClick={(e) => e.stopPropagation()}>
              {canEdit && draft && (
                <Button
                  size="xs"
                  variant="light"
                  loading={setStatus.isPending && setStatus.variables?.id === row.id}
                  onClick={() => setStatus.mutate({ id: row.id!, status: 'ISSUED' })}
                >
                  הנפק
                </Button>
              )}
              {canEdit && draft && (
                <Button
                  size="xs"
                  variant="subtle"
                  color="orange"
                  loading={setStatus.isPending && setStatus.variables?.id === row.id}
                  onClick={() => setStatus.mutate({ id: row.id!, status: 'CANCELLED' })}
                >
                  בטל
                </Button>
              )}
              {/* The server refuses to delete an ISSUED/CORRECTED row — it is a
                  permanent financial record — so the button is not offered for
                  one. Deletes are admin-only across this API. */}
              {canDelete && (draft || row.status === 'CANCELLED') && (
                <Button
                  size="xs"
                  variant="subtle"
                  color="red"
                  loading={removeIssuance.isPending &&
                           removeIssuance.variables === row.id}
                  onClick={() => {
                    void confirmDelete(`הניפוק ${row.issuance_number}`).then(
                      (ok) => ok && removeIssuance.mutate(row.id!))
                  }}
                >
                  מחק
                </Button>
              )}
            </Group>
          )
        },
      },
    ],
    [canEdit, canDelete, setStatus, removeIssuance],
  )

  const bal = balance.data
  // A viewer gets the exact same null the DAO uses on a real failure (DP-01,
  // by design — see the `BudgetBalance` doc comment) so this alone cannot
  // tell the two apart. `isViewer` is what makes it "hidden", never "failed".
  const balanceUnavailable =
    !isViewer && bal != null && (bal.issued == null || bal.balance == null)

  return (
    <Stack gap="md" h="100%" style={{ minHeight: 0 }}>
      <Group justify="space-between">
        <Title order={2}>לקוחות</Title>
        {canEdit && <Button onClick={() => setEditingCustomer(null)}>לקוח חדש</Button>}
      </Group>

      {/* Content-sized, NOT flex-filled: this grid used to claim *all*
          leftover page height (`FILL_PANEL`'s flex-grow, maxHeight="100%"),
          which looked fine when it was the only panel below the title but
          meant two customer rows sat over a near-empty 350px+ box the
          moment it had to share the page with a budgets-then-issuances
          stack below it — measured at 1355x670: the grid took 351px for two
          rows, and its own `withBorder` bottom edge ended up far below the
          last row instead of right after it (the operator's "large empty
          area… no bottom border after the last row"). A `maxHeight` cap with
          no explicit `height` sizes a `DataGrid` to its *content* up to that
          cap — exactly "size to content, cap at ~40%, own scroll past that" —
          and because this Paper no longer flex-grows, its border now sits
          right under the last row by construction, not by extra styling. */}
      <Paper withBorder style={{ minHeight: 180 }}>
        <DataGrid
          data={customers.data ?? []}
          columns={customerColumns}
          loading={customers.isLoading}
          empty="אין לקוחות"
          onRowClick={(c) => { setCustomer(c); setBudget(null) }}
          selected={customer}
          maxHeight="40vh"
        />
      </Paper>

      {/* The one flexible box on this page (`FILL_PANEL`): it takes whatever
          height the capped grid above left behind and scrolls *inside
          itself* if its own content is still taller than that. Budgets and
          issuances are tabs rather than two panels always stacked below the
          grid for exactly this reason — the old layout had *three* boxes
          (grid, budgets, issuances-plus-balance-cards) each wanting a share
          of one fixed-height `Stack`, and the moment their natural sizes
          added up to more than the viewport, something had to give; because
          the grid's flex-basis was 0, the arithmetic gave the *other two*
          panels whatever was left, pushing the issuances panel's bottom
          edge below the scroll container's own bottom edge with nothing
          downstream scrolling it back into view. A tab shows at most one of
          them at a time, so there is only ever one box competing for this
          space, and it is this one — which is allowed to shrink
          (`minHeight: 0`, inside `FILL_PANEL`) and, failing that, scroll. */}
      <Paper withBorder p="sm" style={FILL_PANEL}>
        {!customer ? (
          <Text c="dimmed" size="sm">בחר לקוח מהטבלה למעלה כדי לראות תקציבים וניפוקים.</Text>
        ) : (
          <Tabs
            value={detailTab}
            onChange={(value) => value && setDetailTab(value)}
            style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
          >
            <Tabs.List>
              <Tabs.Tab value="budgets">תקציבים</Tabs.Tab>
              <Tabs.Tab value="issuances">ניפוקים</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="budgets" pt="sm" style={{ ...FILL_REMAINING, overflow: 'auto' }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Title order={5}>תקציבים — {customer.name}</Title>
                  {canEdit && (
                    <Button size="xs" variant="light" onClick={() => setEditingBudget(null)}>
                      תקציב חדש
                    </Button>
                  )}
                </Group>
                {/* Per currency, side by side — never added together. */}
                {summary.data && Object.keys(summary.data.by_currency).length > 0 && (
                  <Group gap="lg" wrap="wrap">
                    {Object.entries(summary.data.by_currency).map(([ccy, totals]) => (
                      <Text key={ccy} size="sm">
                        <b>{ccy}</b> · תקציב {fmtMoney(totals.original, ccy)} · נופק{' '}
                        {fmtMoney(totals.issued, ccy)} ·{' '}
                        <span style={{ color: totals.balance < 0 ? 'var(--mantine-color-red-6)' : undefined }}>
                          יתרה {fmtMoney(totals.balance, ccy)}
                        </span>
                      </Text>
                    ))}
                    <Text size="xs" c="dimmed">סיכום תקציבים פעילים בלבד</Text>
                  </Group>
                )}
                <DataGrid
                  data={budgets.data ?? []}
                  columns={budgetColumns}
                  loading={budgets.isLoading}
                  empty="אין תקציבים ללקוח זה"
                  // Picking a budget also switches to the issuances tab —
                  // the reader's next question is always "what was issued
                  // against it", the same order the old vertical stack
                  // always showed them in.
                  onRowClick={(b) => { setBudget(b); setDetailTab('issuances') }}
                  selected={budget}
                  maxHeight="30vh"
                />
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="issuances" pt="sm" style={{ ...FILL_REMAINING, overflow: 'auto' }}>
              {!budget ? (
                <Text c="dimmed" size="sm">בחר תקציב בלשונית "תקציבים" כדי לראות ניפוקים.</Text>
              ) : (
                <Stack gap="sm">
                  {isViewer ? (
                    // Not an Alert in `red`: nothing failed. Framing a deliberate,
                    // by-design redaction as an error is its own way of rendering a
                    // false number — just in words instead of digits.
                    <Alert color="gray" variant="light" title="פרטי תקציב מוגבלים">
                      תקציב, ניצול ויתרה מוצגים למנהלים ולעורכים בלבד.
                    </Alert>
                  ) : balanceUnavailable ? (
                    <Alert color="red" variant="light" title="לא ניתן לחשב את היתרה">
                      {bal?.error || 'חישוב היתרה נכשל.'} היתרה אינה מוצגת כדי שלא תיקרא
                      בטעות כתקציב פנוי.
                    </Alert>
                  ) : (
                    <SimpleGrid cols={{ base: 1, sm: 3 }}>
                      <Card withBorder padding="sm">
                        <Text size="xs" c="dimmed">תקציב מקורי</Text>
                        <Text fw={700} size="lg">{fmtMoney(bal?.original, bal?.currency)}</Text>
                      </Card>
                      <Card withBorder padding="sm">
                        <Text size="xs" c="dimmed">נופק (ISSUED בלבד)</Text>
                        <Text fw={700} size="lg">{fmtMoney(bal?.issued, bal?.currency)}</Text>
                      </Card>
                      <Card withBorder padding="sm">
                        <Text size="xs" c="dimmed">יתרה</Text>
                        <Text
                          fw={700}
                          size="lg"
                          c={(bal?.balance ?? 0) < 0 ? 'red' : undefined}
                        >
                          {fmtMoney(bal?.balance, bal?.currency)}
                        </Text>
                      </Card>
                    </SimpleGrid>
                  )}

                  <Group justify="space-between">
                    <Title order={5}>ניפוקים — {budget.name}</Title>
                    <Group gap="sm">
                      <Text size="xs" c="dimmed">
                        טיוטה אינה מנכה מהתקציב; רק ניפוק שהונפק.
                      </Text>
                      {canEdit && (
                        <Button size="xs" variant="light" onClick={() => setEditingIssuance(null)}>
                          ניפוק חדש
                        </Button>
                      )}
                    </Group>
                  </Group>
                  <DataGrid
                    data={issuances.data ?? []}
                    columns={issuanceColumns}
                    loading={issuances.isLoading || loadingIssuance}
                    empty="אין ניפוקים לתקציב זה"
                    onRowClick={openIssuance}
                    maxHeight="35vh"
                  />
                </Stack>
              )}
            </Tabs.Panel>
          </Tabs>
        )}
      </Paper>

      <CustomerModal
        value={editingCustomer}
        onClose={() => setEditingCustomer(undefined)}
      />
      <BudgetModal
        value={editingBudget}
        customerId={customer?.id ?? null}
        onClose={() => setEditingBudget(undefined)}
      />
      {customer && (
        <IssuanceEditor
          opened={editingIssuance !== undefined}
          onClose={() => setEditingIssuance(undefined)}
          customerId={customer.id!}
          budgets={budgets.data ?? []}
          preferredBudgetId={budget?.id ?? null}
          issuance={editingIssuance ?? null}
        />
      )}
    </Stack>
  )
}

function CustomerModal({
  value, onClose,
}: { value: Customer | null | undefined; onClose: () => void }) {
  const qc = useQueryClient()
  const [draft, setDraft] = useState<Partial<Customer>>({})
  const [error, setError] = useState<string | null>(null)
  const opened = value !== undefined

  useMemo(() => {
    if (opened) { setDraft(value ? { ...value } : { name: '', status: 'ACTIVE' }); setError(null) }
  }, [opened, value])

  const save = useMutation({
    mutationFn: (c: Partial<Customer>) =>
      c.id ? api.put(`/customers/${c.id}`, c) : api.post('/customers', c),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customers'] })
      notifications.show({ message: 'הלקוח נשמר', color: 'green' })
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'השמירה נכשלה.'),
  })

  function submit() {
    if (!draft.name?.trim()) return setError('יש להזין שם לקוח.')
    save.mutate(draft)
  }

  return (
    <Modal opened={opened} onClose={onClose} title={value ? 'עריכת לקוח' : 'לקוח חדש'}>
      <form onSubmit={(e) => { e.preventDefault(); submit() }}>
      <Stack>
        {error && <Alert color="red" variant="light">{error}</Alert>}
        <TextInput label="שם" required value={draft.name ?? ''} onChange={(e) => {
          // Read .value from the event synchronously, before it reaches the
          // setState updater below — not pre-existing style, a real bug this
          // session's ux:check run found: a DOM Event's `currentTarget` is
          // nulled by the browser once dispatch finishes, and the updater
          // closure can run after that point, throwing
          // "Cannot read properties of null (reading 'value')" — reproduced
          // reliably via Playwright's `fill()`, which is exactly how
          // uxcheck.mjs exercises this field. Every other screen in this app
          // reads `.value` immediately for the same reason; this one field
          // did not.
          const name = e.currentTarget.value
          setDraft((d) => ({ ...d, name }))
        }} />
        <TextInput label="מזהה חיצוני" value={draft.external_id ?? ''} onChange={(e) => {
          const v = e.currentTarget.value
          setDraft((d) => ({ ...d, external_id: v }))
        }} />
        <TextInput label="איש קשר" value={draft.contact_name ?? ''} onChange={(e) => {
          const v = e.currentTarget.value
          setDraft((d) => ({ ...d, contact_name: v }))
        }} />
        <TextInput label="טלפון" value={draft.contact_phone ?? ''} onChange={(e) => {
          const v = e.currentTarget.value
          setDraft((d) => ({ ...d, contact_phone: v }))
        }} />
        <TextInput label='דוא"ל' value={draft.contact_email ?? ''} onChange={(e) => {
          const v = e.currentTarget.value
          setDraft((d) => ({ ...d, contact_email: v }))
        }} />
        <Textarea label="הערות" autosize minRows={2} value={draft.notes ?? ''} onChange={(e) => {
          const v = e.currentTarget.value
          setDraft((d) => ({ ...d, notes: v }))
        }} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>ביטול</Button>
          <Button type="submit" loading={save.isPending}>שמור</Button>
        </Group>
      </Stack>
      </form>
    </Modal>
  )
}

function BudgetModal({
  value, customerId, onClose,
}: { value: CustomerBudget | null | undefined; customerId: number | null; onClose: () => void }) {
  const qc = useQueryClient()
  const [draft, setDraft] = useState<Partial<CustomerBudget>>({})
  const [error, setError] = useState<string | null>(null)
  const opened = value !== undefined

  useMemo(() => {
    if (opened) {
      setDraft(value ? { ...value } : {
        customer_id: customerId ?? undefined, name: '', currency: 'USD',
        original_amount: 0, status: 'ACTIVE',
      })
      setError(null)
    }
  }, [opened, value, customerId])

  const save = useMutation({
    mutationFn: (b: Partial<CustomerBudget>) =>
      b.id ? api.put(`/budgets/${b.id}`, b) : api.post('/budgets', b),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['budgets'] })
      notifications.show({ message: 'התקציב נשמר', color: 'green' })
      onClose()
    },
    // The currency change is refused once issuances are posted; the server's
    // message explains why, and it is worth showing verbatim.
    onError: (err) => setError(err instanceof ApiError ? err.message : 'השמירה נכשלה.'),
  })

  function submit() {
    if (!draft.name?.trim()) return setError('יש להזין שם תקציב.')
    save.mutate(draft)
  }

  return (
    <Modal opened={opened} onClose={onClose} title={value ? 'עריכת תקציב' : 'תקציב חדש'}>
      <form onSubmit={(e) => { e.preventDefault(); submit() }}>
      <Stack>
        {error && <Alert color="red" variant="light">{error}</Alert>}
        <TextInput label="שם תקציב" required value={draft.name ?? ''} onChange={(e) => {
          const v = e.currentTarget.value
          setDraft((d) => ({ ...d, name: v }))
        }} />
        <Group grow>
          <NumberInput hideControls label="סכום" min={0} decimalScale={2} thousandSeparator="," value={draft.original_amount ?? 0} onChange={(v) => setDraft((d) => ({ ...d, original_amount: Number(v) || 0 }))} />
          <Select label="מטבע" data={['USD', 'ILS']} value={draft.currency ?? 'USD'} onChange={(v) => setDraft((d) => ({ ...d, currency: v ?? 'USD' }))} />
        </Group>
        <Group grow>
          <NumberInput hideControls
            label="תעריף הנדסי %"
            min={0}
            max={100}
            decimalScale={2}
            /* Stored as a fraction (0.10 = 10%), like discount_pct. */
            value={Number(((draft.engineering_tariff_pct ?? 0) * 100).toFixed(2))}
            onChange={(v) => setDraft((d) => ({ ...d, engineering_tariff_pct: (Number(v) || 0) / 100 }))}
          />
          <TextInput label="EMF" value={draft.emf_number ?? ''} onChange={(e) => {
            const v = e.currentTarget.value
            setDraft((d) => ({ ...d, emf_number: v }))
          }} />
        </Group>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>ביטול</Button>
          <Button type="submit" loading={save.isPending}>שמור</Button>
        </Group>
      </Stack>
      </form>
    </Modal>
  )
}
